// src/components/sourcex/SourcePreviewField.jsx
//
// SourceX — WYSIWYG preview of one question, exactly as it prints.
//
// The template editor used to preview a question with QuestionField, i.e. the
// interactive control a coordinator fills in for a patient. That answers the
// wrong question: a <select> showing "Select" tells a reviewer nothing about
// what lands in the exported Word/PDF source document, where that same field is
// a row of "☐ Yes ☐ No ☐ N/A" checkboxes.
//
// This component renders the PAPER form instead — the blank, fillable layout
// produced by services/sourceDocExport.js. It is deliberately a mirror of that
// module's `answerArea` / `tableHtml`: same option lists, same fill-line widths,
// same format hints, same "4 blank rows" table rule. Read them together; a
// change to the export belongs here too.
//
// Presentation only. It has no value, no onChange and no state — nothing here
// can write to a template or to patient data.

import {
  normFieldType,
  makeColumn,
  columnPrintsBoxes,
  columnIsCheckbox,
  columnChoices,
  choiceMarkFor,
  checkboxIsTicked,
  isCompositeTable,
  tablePrintRowCount,
  cellSelection,
} from "../../services/templateSchema";

/** A blank fill line — the handwriting space on a paper source. */
const Blank = ({ w = "12rem" }) => (
  <span
    className="inline-block align-bottom border-b border-slate-400/70 h-[0.95em]"
    style={{ minWidth: w }}
  />
);

/** One printed choice mark — checked when a coordinator-set default pre-checks
 * it (never a patient's actual answer; this component never has one).
 *
 * Square for a tick-any list, round for a pick-one list, exactly as both
 * exports draw it. The shape is the only thing on the printed page that tells
 * a coordinator whether they may mark more than one, so it has to be right
 * here too — see choiceMarkFor. */
const Box = ({ label, checked = false, style = "box" }) => {
  const round = style === "radio";
  return (
    <span className="inline-flex items-center gap-1.5 mr-4 whitespace-nowrap text-slate-200">
      <span
        className={`inline-flex items-center justify-center w-3.5 h-3.5 border border-slate-400/80 text-[10px] leading-none ${
          round ? "rounded-full" : "rounded-[2px]"
        }`}
      >
        {checked ? (
          round ? (
            <span className="w-1.5 h-1.5 rounded-full bg-slate-200" />
          ) : (
            "✓"
          )
        ) : (
          ""
        )}
      </span>
      {label}
    </span>
  );
};

const Hint = ({ children }) => (
  <span className="ml-1.5 text-[11px] text-slate-500">{children}</span>
);

// Plain-text version of the export's `conditionText`. Kept local (rather than
// imported) because the export escapes values for HTML — React must not render
// "&amp;". Same shapes, same wording.
const conditionText = (cond, labelById) => {
  if (!cond || typeof cond !== "object") return "";
  const join = (arr, sep) =>
    arr.map((c) => conditionText(c, labelById)).filter(Boolean).join(sep);
  if (Array.isArray(cond.all)) return join(cond.all, " and ");
  if (Array.isArray(cond.any)) return join(cond.any, " or ");
  if (cond.not) {
    const inner = conditionText(cond.not, labelById);
    return inner ? `not (${inner})` : "";
  }
  if (cond.questionId) {
    const lbl = labelById?.get(cond.questionId) || "a previous answer";
    const op =
      { eq: "=", neq: "≠", gt: ">", gte: "≥", lt: "<", lte: "≤" }[cond.op] || cond.op;
    const val = Array.isArray(cond.value) ? cond.value.join(", ") : cond.value;
    if (cond.op === "isBlank") return `${lbl} is blank`;
    if (cond.op === "notBlank") return `${lbl} is answered`;
    return `${lbl} ${op} ${val}`;
  }
  return "";
};

/** Blank table grid — header row plus the same row count the export prints. */
function PreviewTable({ question }) {
  const cols = (Array.isArray(question.columns) ? question.columns : []).map((c) =>
    makeColumn(c)
  );
  if (!cols.length) {
    return (
      <p className="mt-1 text-xs text-rose-300 italic">
        This table has no columns defined — it exports as an empty block. Edit the
        template to add columns.
      </p>
    );
  }

  // A fixed-row table prints its labels; everything else prints blank lines
  // to write on (honour minRows, but never fewer than 4, and never more than
  // maxRows — see tablePrintRowCount).
  const rowLabels = Array.isArray(question.rowLabels) ? question.rowLabels : [];
  const fixedRows = rowLabels.length > 0;
  const rowCount = tablePrintRowCount(question);
  const prefillRows = Array.isArray(question.prefillRows) ? question.prefillRows : [];

  // A cell's printed content: a coordinator-set default if one was set,
  // otherwise the usual blank tick-boxes / write-in space. A default for a
  // choice column (Select/MultiSelect/YesNo-Maybe/Checkbox) PRE-CHECKS that
  // option rather than replacing the boxes with text — the boxes are still
  // the actual answer choices, one of them is just ticked to start. A
  // MultiSelect (checklist) default can pre-check SEVERAL boxes — cellSelection
  // handles both that array and an older single-value default identically.
  const cellContent = (c, ri = 0) => {
    const prefill = prefillRows[ri]?.[c.id];
    if (columnIsCheckbox(c)) return prefill ? "☑" : "☐";
    if (columnPrintsBoxes(c)) {
      const selected = cellSelection(prefill);
      // Same round/square rule as a standalone choice question, so a dropdown
      // column reads as pick-one inside a table too. See choiceMarkFor.
      const round = choiceMarkFor(c.type) === "radio";
      const glyph = (on) => (round ? (on ? "●" : "○") : on ? "☑" : "☐");
      return columnChoices(c)
        .map((o) => `${glyph(selected.includes(String(o)))} ${o}`)
        .join("  ");
    }
    return prefill ? (
      <span className="font-medium text-slate-100">
        {Array.isArray(prefill) ? prefill.join(", ") : prefill}
      </span>
    ) : (
      ""
    );
  };

  // A composite question — several answer pieces bundled under one question
  // via the field-type palette — is a real Table (one row, no row labels)
  // under the hood, but reads far better as a stack of labeled lines than as
  // a one-row grid with column headers running across the page.
  //
  // Each line is drawn by SourcePreviewField itself — the SAME renderer a
  // standalone question of that type gets — not by cellContent. A table cell is
  // a box on a grid, so it draws no fill line and no format hint: the grid's
  // ruling is the line and the column header carries the hint. A composite has
  // neither, so routing its pieces through cellContent printed a bold label and
  // then nothing at all for every write-in type — a Date lost its line and its
  // "(DD-Mon-YYYY)", a Number lost its line, a Long text lost all three of its
  // lines, so every write-in piece looked identical whichever type was picked.
  // Choice pieces already drew their marks and are unchanged. Mirrors
  // compositeTableHtml in sourceDocExport, which is what actually prints.
  //
  // `unit` is dropped from the piece because the label beside it already prints
  // it ("Weight (kg)"), and the Number case would otherwise repeat it.
  if (isCompositeTable(question)) {
    const prefillRow = prefillRows[0] || {};
    return (
      <div className="mt-1.5 rounded-lg border border-white/15 divide-y divide-white/10 overflow-hidden">
        {cols.map((c) => (
          <div key={c.id} className="flex items-start gap-3 px-2.5 py-1.5">
            <span className="w-32 shrink-0 pt-0.5 text-[11px] font-semibold text-slate-200">
              {c.label || "(untitled)"}
              {c.unit ? (
                <span className="ml-1 font-normal text-slate-500">({c.unit})</span>
              ) : null}
            </span>
            <div className="min-w-0 flex-1 text-[11px] text-slate-300">
              <SourcePreviewField
                question={{ ...c, unit: "", defaultValue: prefillRow[c.id] }}
                hideConditionNote
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-1.5 max-w-full overflow-x-auto rounded-lg border border-white/15">
      <table className="w-max min-w-full text-sm border-collapse">
        <thead>
          <tr className="bg-white/[0.06]">
            {fixedRows && (
              <th className="border border-white/15 text-left px-2.5 py-1.5 text-[11px] font-semibold text-slate-200 align-bottom min-w-[8rem]">
                {question.rowLabelHeader || ""}
              </th>
            )}
            {cols.map((c) => (
              <th
                key={c.id}
                className="border border-white/15 text-left px-2.5 py-1.5 text-[11px] font-semibold text-slate-200 align-bottom min-w-[8rem]"
              >
                {c.label}
                {c.unit ? (
                  <span className="ml-1 font-normal text-slate-500">({c.unit})</span>
                ) : null}
                {/* A choice column too long to print boxes still has to declare
                    its choices here — approving a template means signing off on
                    exactly what a coordinator will be offered, and the printed
                    cell for these is a write-in with the codes in the legend. */}
                {Array.isArray(c.options) &&
                  c.options.length > 0 &&
                  !columnPrintsBoxes(c) && (
                    <div className="mt-0.5 font-normal normal-case text-[10px] leading-tight text-slate-400">
                      {c.options.join(" · ")}
                    </div>
                  )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }).map((_, ri) => (
            // Blank print rows — positional by nature, they carry no data.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={ri}>
              {fixedRows && (
                <td className="border border-white/15 px-2.5 h-7 min-w-[8rem] text-slate-200">
                  {rowLabels[ri]}
                </td>
              )}
              {/* Cells mirror the export exactly: tick boxes for a short
                  choice column, a lone box for a checkbox column, and blank
                  writing space for everything else. */}
              {cols.map((c) => (
                <td
                  key={c.id}
                  className="border border-white/15 px-2.5 h-7 min-w-[8rem] whitespace-nowrap text-[11px] text-slate-300"
                >
                  {cellContent(c, ri)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-2.5 py-1 text-[10px] text-slate-500 border-t border-white/10">
        {fixedRows
          ? `${rowCount} fixed row${rowCount === 1 ? "" : "s"} print here`
          : `${rowCount} blank row${rowCount === 1 ? "" : "s"} print here`}
      </div>
    </div>
  );
}

/**
 * The fillable area for one question, as it will be exported.
 *
 * @param {object} props.question    a v2 question
 * @param {Map}    [props.labelById] question id -> label, for "only if" notes
 * @param {boolean} [props.hideConditionNote] suppress the "only if …" line when
 *   the surrounding UI already states the condition (the template editor does),
 *   so the same rule isn't printed twice under one field.
 */
export default function SourcePreviewField({
  question,
  labelById,
  hideConditionNote = false,
}) {
  const q = question || {};
  const type = normFieldType(q.type);
  const opts = Array.isArray(q.options) ? q.options : [];
  // A coordinator-set default for the BLANK form — never a patient's actual
  // answer, this component never has one. Same idea as a Table's prefillRows,
  // just for a single (non-Table) question.
  const defaultSelected = cellSelection(q.defaultValue);

  const cond = hideConditionNote ? "" : conditionText(q.visibleWhen, labelById);
  const condNote = cond ? (
    <p className="mt-0.5 text-[11px] italic text-fuchsia-200/80">only if {cond}</p>
  ) : null;

  const area = (() => {
    switch (type) {
      case "YesNo/Maybe":
        return (
          <div className="flex flex-wrap items-center">
            <Box label="Yes" checked={defaultSelected.includes("Yes")} />
            <Box label="No" checked={defaultSelected.includes("No")} />
            <Box label="N/A" checked={defaultSelected.includes("N/A")} />
          </div>
        );

      case "Checkbox":
        // ONE box, no labels — see the Checkbox case in sourceDocExport.
        return (
          <div className="flex flex-wrap items-center">
            <Box label="" checked={checkboxIsTicked(q.defaultValue)} />
          </div>
        );

      case "Select":
        return opts.length ? (
          // Round marks — a dropdown is pick-ONE. See choiceMarkFor.
          <div className="flex flex-wrap items-center gap-y-1">
            {opts.map((o) => (
              <Box
                key={o}
                label={o}
                style="radio"
                checked={defaultSelected.includes(String(o))}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-rose-300 italic">
            No choices defined — this exports as an empty option list.
          </p>
        );

      case "MultiSelect":
        return opts.length ? (
          <div className="flex flex-wrap items-center gap-y-1">
            <span className="mr-2 text-[11px] text-slate-500">(check all that apply)</span>
            {opts.map((o) => (
              <Box key={o} label={o} checked={defaultSelected.includes(String(o))} />
            ))}
          </div>
        ) : (
          <p className="text-xs text-rose-300 italic">
            No checklist items defined — this exports as an empty list.
          </p>
        );

      case "Number":
        return (
          <span className="inline-flex items-end">
            {q.defaultValue ? (
              <span className="font-medium text-slate-100">{q.defaultValue}</span>
            ) : (
              <Blank w="8rem" />
            )}
            {q.unit ? <span className="ml-1.5 text-slate-400">{q.unit}</span> : null}
          </span>
        );

      case "Date":
        return (
          <span className="inline-flex items-end">
            {q.defaultValue ? (
              <span className="font-medium text-slate-100">{q.defaultValue}</span>
            ) : (
              <Blank w="10rem" />
            )}
            <Hint>(DD-Mon-YYYY)</Hint>
          </span>
        );

      case "Time":
        return (
          <span className="inline-flex items-end">
            {q.defaultValue ? (
              <span className="font-medium text-slate-100">{q.defaultValue}</span>
            ) : (
              <Blank w="8rem" />
            )}
            <Hint>(HH:MM)</Hint>
          </span>
        );

      case "DateTime":
        return (
          <span className="inline-flex items-end">
            {q.defaultValue ? (
              <span className="font-medium text-slate-100">{q.defaultValue}</span>
            ) : (
              <Blank w="14rem" />
            )}
            <Hint>(DD-Mon-YYYY HH:MM)</Hint>
          </span>
        );

      case "TextArea":
        // Three write-in lines, matching what both exports print — a box was
        // a shape neither of them draws, and it made Long text look like a
        // different kind of field rather than a longer one.
        return q.defaultValue ? (
          <span className="font-medium text-slate-100 whitespace-pre-wrap">
            {q.defaultValue}
          </span>
        ) : (
          <span className="block">
            <Blank w="100%" />
            <Blank w="100%" />
            <Blank w="100%" />
          </span>
        );

      case "Signature":
        return (
          <div className="rounded-lg border border-dashed border-white/20 bg-white/[0.02] px-3 py-2.5">
            {q.signatureRole && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                {q.signatureRole}
              </div>
            )}
            {q.attestation && (
              <p className="mt-1 text-xs text-slate-400">{q.attestation}</p>
            )}
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <span className="inline-flex items-end">
                <Blank w="16rem" />
                <Hint>Signature</Hint>
              </span>
              <span className="inline-flex items-end">
                <Blank w="10rem" />
                <Hint>Date</Hint>
              </span>
            </div>
          </div>
        );

      case "Table":
        return <PreviewTable question={q} />;

      case "Header":
        // Headings are drawn by the editor itself; nothing to preview here.
        return null;

      case "Text":
      default:
        return q.defaultValue ? (
          <span className="font-medium text-slate-100">{q.defaultValue}</span>
        ) : (
          <Blank w="16rem" />
        );
    }
  })();

  if (!area && !condNote) return null;

  return (
    <div className="mt-1">
      {condNote}
      {area}
    </div>
  );
}
